import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useCategoriesFlat, useCreateCategory, useDeleteCategory } from '@/hooks/use-categories';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

const Schema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'kebab-case lowercase'),
  parentId: z.string().nullable(),
});

type FormValues = z.infer<typeof Schema>;

export function CategoriesPage() {
  const t = useT();
  const cats = useCategoriesFlat();
  const create = useCreateCategory();
  const del = useDeleteCategory();
  const [error, setError] = useState<string | null>(null);
  // RL-148: start FOLDED. RL-52 started expanded to preserve the old page's
  // density, which stopped being readable once real catalogues arrived — a
  // GP-loaded tenant opens this page onto thousands of rows and has to collapse
  // its way back to the roots before it can navigate at all. Folded, the page
  // opens on the top level and an operator expands the one branch they want.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  // Seeded once, when the tree first arrives — not on every render. A refetch
  // must never re-fold branches the operator has deliberately opened.
  const seededFold = useRef(false);
  useEffect(() => {
    if (seededFold.current || cats.flat.length === 0) return;
    seededFold.current = true;
    setCollapsed(new Set(cats.flat.filter((c) => c.hasChildren).map((c) => c.id)));
  }, [cats.flat]);
  const parentIds = cats.flat.filter((c) => c.hasChildren).map((c) => c.id);
  const visibleCategories = cats.flat.filter(
    (category) => !category.ancestorIds.some((ancestorId) => collapsed.has(ancestorId)),
  );

  function toggleCategory(id: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { name: '', slug: '', parentId: null },
  });

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('catalog.categories.title', 'Categories')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'catalog.categories.subtitle',
            '{count} total · root-level slugs must be unique; per-parent slugs must be unique within siblings.',
          ).replace('{count}', String(cats.flat.length))}
        </p>
      </header>

      <section className="rounded-md border bg-background p-5">
        <h2 className="mb-3 text-sm font-medium">
          {t('catalog.categories.add', 'Add a category')}
        </h2>
        <form
          className="grid gap-3 md:grid-cols-[2fr_1fr_2fr_auto]"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            try {
              await create.mutateAsync({
                name: values.name,
                slug: values.slug,
                parentId: values.parentId === '' ? null : values.parentId,
              });
              reset({ name: '', slug: '', parentId: null });
            } catch (err) {
              setError(extractError(err));
            }
          })}
        >
          <div className="space-y-1">
            <Label>{t('catalog.common.name', 'Name')}</Label>
            <Input
              {...register('name')}
              onBlur={(e) => {
                // Auto-fill slug from name if blank
                const v = e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-+|-+$/g, '');
                if (v !== '') setValue('slug', v, { shouldValidate: true });
              }}
            />
            {errors.name?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('catalog.common.slug', 'Slug')}</Label>
            <Input className="font-mono" {...register('slug')} />
            {errors.slug?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.slug.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('catalog.categories.parent', 'Parent')}</Label>
            <Select {...register('parentId')}>
              <option value="">{t('catalog.categories.rootOption', '— root —')}</option>
              {cats.flat.map((c) => (
                <option key={c.id} value={c.id}>
                  {'— '.repeat(c.depth)}
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={create.isPending}>
              {t('catalog.categories.addButton', 'Add')}
            </Button>
          </div>
        </form>
        {error !== null && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </section>

      <section className="rounded-md border bg-background">
        {/*
          RL-148 folds the tree by default, so give back a one-click way to see
          everything — otherwise the operator who WANTED the old expanded view
          has to open every branch by hand.
        */}
        <div className="flex justify-end gap-2 border-b px-3 py-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={parentIds.length === 0 || collapsed.size === 0}
            onClick={() => setCollapsed(new Set())}
          >
            {t('catalog.categories.expandAll', 'Expand all')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={parentIds.length === 0 || collapsed.size === parentIds.length}
            onClick={() => setCollapsed(new Set(parentIds))}
          >
            {t('catalog.categories.collapseAll', 'Collapse all')}
          </Button>
        </div>
        <Table>
          <THead>
            <TR>
              <TH>{t('catalog.categories.path', 'Path')}</TH>
              <TH>{t('catalog.categories.externalCode', 'External code')}</TH>
              <TH>{t('catalog.common.slug', 'Slug')}</TH>
              <TH className="w-24" />
            </TR>
          </THead>
          <TBody>
            {cats.isLoading && (
              <TR>
                <TD colSpan={4} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {visibleCategories.map((c) => (
              <TR key={c.id}>
                <TD>
                  <span
                    className="inline-flex items-center gap-1"
                    style={{ paddingLeft: `${c.depth * 16}px` }}
                  >
                    {c.hasChildren ? (
                      <button
                        type="button"
                        className="inline-flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-expanded={!collapsed.has(c.id)}
                        aria-label={`${
                          collapsed.has(c.id)
                            ? t('catalog.categories.expand', 'Expand')
                            : t('catalog.categories.collapse', 'Collapse')
                        } ${c.name}`}
                        onClick={() => toggleCategory(c.id)}
                      >
                        <span aria-hidden="true">{collapsed.has(c.id) ? '▸' : '▾'}</span>
                      </button>
                    ) : (
                      <span className="inline-block size-6" aria-hidden="true" />
                    )}
                    <span>{c.name}</span>
                  </span>
                </TD>
                <TD className="font-mono text-xs">{c.externalCode ?? '—'}</TD>
                <TD className="font-mono text-xs text-muted-foreground">{c.slug}</TD>
                <TD className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={del.isPending}
                    onClick={() => {
                      setError(null);
                      del.mutateAsync(c.id).catch((err: unknown) => setError(extractError(err)));
                    }}
                  >
                    {t('catalog.common.delete', 'Delete')}
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </section>
    </div>
  );
}

// RFL-201: use the shared extractor so category-create surfaces the
// backend's actual validation issue(s) instead of a generic
// "Validation failed".
const extractError = extractApiError;
